import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, MessageSquareText, Loader2 } from "lucide-react";
import { PHONE, PHONE_RAW } from "./shared";
import TermsAcceptance from "./TermsAcceptance";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-stone-700 bg-[#0c0a09] px-4 py-3 text-base sm:text-sm text-bone placeholder:text-stone-600 focus:border-amber-500 focus:outline-none transition-colors duration-150";
const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400";

export default function QuotePage() {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [terms, setTerms] = useState(null);
  const [accept, setAccept] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/quotes/${id}`)
      .then((r) => setQuote(r.data))
      .catch(() => setError("notfound"));
    axios
      .get(`${API}/terms`)
      .then((r) => setTerms(r.data))
      .catch(() => {});
  }, [id]);

  const canSubmit = name.trim() && phone.trim() && terms && accept?.valid;

  const claim = async (e) => {
    e.preventDefault();
    setClaiming(true);
    setError("");
    try {
      await axios.post(`${API}/quotes/${id}/claim`, {
        name: name.trim(),
        phone: phone.trim(),
        agreed_terms: accept.agreed_terms,
        private_lines: accept.private_lines,
        private_lines_other: accept.private_lines_other,
        photo_opt_out: accept.photo_opt_out,
      });
      setClaimed(true);
    } catch (err) {
      if (err.response?.status === 409) {
        setQuote((q) => ({ ...q, status: "claimed" }));
      } else {
        setError("claim");
      }
    }
    setClaiming(false);
  };

  return (
    <div data-testid="public-quote-page" className="flex min-h-screen items-center justify-center px-4 py-16">
      <div className="w-full max-w-lg">
        <p className="text-center font-display text-3xl font-black uppercase tracking-tight text-bone">
          Friday<span className="text-amber-500">Stumps</span>
        </p>
        <p className="mt-1 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-stone-500">
          Fridays only · 100% contactless · Columbus, OH
        </p>

        {!quote && !error && (
          <p data-testid="quote-loading" className="mt-16 flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-stone-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your quote…
          </p>
        )}

        {error === "notfound" && (
          <div data-testid="quote-notfound" className="mt-10 border border-stone-800 bg-charcoal p-8 text-center">
            <p className="font-display text-2xl font-black uppercase text-bone">This quote flew the coop.</p>
            <p className="mt-3 text-sm text-stone-400">
              The link may be old — text a photo of your stump to {PHONE} and you'll get a fresh flat quote within hours.
            </p>
          </div>
        )}

        {quote && (
          <div className="mt-10 border border-stone-800 bg-charcoal">
            <div className="border-b border-stone-800 bg-amber-500/5 px-6 py-4">
              <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-500">
                Your personalized quote · left by Eric
              </p>
            </div>
            <div className="p-6 sm:p-8">
              <p data-testid="quote-headline" className="font-display text-2xl font-black uppercase leading-tight text-bone sm:text-3xl">
                That stump at<br />
                <span className="text-amber-500">{quote.address}</span>
              </p>
              <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 font-mono text-xs uppercase tracking-[0.15em] text-stone-400">
                <span data-testid="quote-width">≈ {quote.stump_width_in}″ wide</span>
                <span>Grind day: this Friday</span>
              </div>
              {quote.notes && (
                <p className="mt-4 border-l-2 border-amber-500/50 pl-3 text-sm italic text-stone-400">{quote.notes}</p>
              )}
              <p data-testid="quote-price" className="mt-6 font-display text-5xl font-black tracking-tight text-bone">
                ${quote.price}
                <span className="ml-2 align-middle font-mono text-[11px] font-normal uppercase tracking-[0.2em] text-stone-500">
                  flat · all in
                </span>
              </p>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-500">
                Priced for this stump, for you — not off a menu.
              </p>

              {claimed || quote.status === "claimed" ? (
                <div data-testid="quote-claimed-panel" className="mt-8 border border-green-700/50 bg-green-500/5 p-6">
                  <p className="flex items-center gap-2 font-display text-xl font-black uppercase text-bone">
                    <CheckCircle2 className="h-5 w-5 text-green-400" />
                    {quote.status === "claimed" && !claimed
                      ? `Already claimed${quote.claimed_name ? ` by ${quote.claimed_name}` : ""}`
                      : "Locked in."}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-stone-400">
                    Eric has your details and texts to confirm the Friday slot. Leave the gate unlocked — you'll get a photo when it's flat ground.
                  </p>
                  {claimed && (
                    <p data-testid="claim-terms-note" className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-green-400/80">
                      You've accepted the quote and agreed to our terms of service.
                    </p>
                  )}
                  <p data-testid="claim-811-note" className="mt-4 border border-amber-500/40 bg-amber-500/5 px-4 py-3 font-mono text-[11px] leading-relaxed tracking-[0.05em] text-amber-400">
                    Next: Eric calls 811 for you — utility crews mark underground lines within ~3 days, by law, before any grinding. If marking runs past Thursday, your job simply rolls to the following Friday. Nothing for you to do.
                  </p>
                </div>
              ) : (
                <form data-testid="quote-claim-form" onSubmit={claim} className="mt-8 space-y-4">
                  <p className="text-sm leading-relaxed text-stone-400">
                    Want it gone? Claim this quote — name, number, and a quick terms check. No account, no card, no site visit.
                  </p>
                  <div>
                    <label className={labelCls} htmlFor="claim-name">Your name *</label>
                    <input id="claim-name" data-testid="claim-name-input" required className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Buckeye" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="claim-phone">Phone / SMS *</label>
                    <input id="claim-phone" data-testid="claim-phone-input" required type="tel" className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="614-555-0139" />
                  </div>

                  <TermsAcceptance terms={terms} onChange={setAccept} />

                  {error === "claim" && (
                    <p data-testid="claim-error" className="border border-red-700/50 bg-red-500/10 px-4 py-3 font-mono text-xs text-red-400">
                      Something glitched — text {PHONE} instead.
                    </p>
                  )}
                  <button data-testid="claim-submit-btn" type="submit" disabled={claiming || !canSubmit} className="min-h-[52px] w-full bg-amber-500 px-6 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400 active:bg-amber-600 disabled:opacity-60">
                    {claiming ? "Claiming…" : "Accept quote & agree to terms"}
                  </button>
                </form>
              )}

              <a data-testid="quote-text-link" href={`sms:${PHONE_RAW}`} className="mt-5 flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-500 transition-colors duration-150 hover:text-amber-500">
                <MessageSquareText className="h-3.5 w-3.5" /> Rather text? {PHONE}
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
